import {
    GameType, GAME_TYPES, GAME_INITIALS, CLASSIC_GAME_TYPES, gameTypesToScope,
} from '../persistence/types.js';
import { getGameTitle } from '../games/GameRegistry.js';

/**
 * Two rows of game chips: the 12 in the set on top, the pool remainder below. Click a
 * chip in one row, then a chip in the other, to swap them — keeping the set at 12.
 *
 * Owns its own root element and re-renders in place, so a host can drop it into a modal
 * without rebuilding itself on every chip click. Shared by the Grid Creator (choose the
 * set for a new grid) and the Duplicate modal (change the set of a copy).
 */
export class GameSetPicker {
    private root: HTMLElement;
    /** The 12-game set (canonical order). */
    private selected: GameType[];
    /** A chip awaiting its swap partner from the other row (null = none pending). */
    private pendingSwap: GameType | null = null;
    private onChange?: (set: GameType[]) => void;

    constructor(initial: GameType[] = CLASSIC_GAME_TYPES, onChange?: (set: GameType[]) => void) {
        this.root = document.createElement('div');
        this.root.className = 'set-picker';
        this.selected = [...initial];
        this.onChange = onChange;
        this.render();
    }

    /** The picker's root element, to append into a host modal. */
    get element(): HTMLElement {
        return this.root;
    }

    getSelection(): GameType[] {
        return [...this.selected];
    }

    /** The selection as a canonical scope string, e.g. "ABCDFIKLMOSW". */
    getScope(): string {
        return gameTypesToScope(this.selected);
    }

    setSelection(set: GameType[]): void {
        this.selected = [...set];
        this.pendingSwap = null;
        this.render();
    }

    private render(): void {
        this.root.innerHTML = '';

        const title = document.createElement('div');
        title.className = 'set-picker-title';
        title.textContent = this.pendingSwap
            ? `Swapping ${getGameTitle(this.pendingSwap)} — pick a game from the other row`
            : 'Games in this grid — click one, then a game from the other row to swap';
        this.root.appendChild(title);

        const inSet = new Set(this.selected);
        this.root.appendChild(this.renderChipRow(this.selected, true));

        const sep = document.createElement('div');
        sep.className = 'set-picker-sep';
        sep.textContent = 'not in this grid';
        this.root.appendChild(sep);

        this.root.appendChild(this.renderChipRow(GAME_TYPES.filter(g => !inSet.has(g)), false));
    }

    private renderChipRow(games: GameType[], inSet: boolean): HTMLElement {
        const row = document.createElement('div');
        row.className = 'set-picker-row';
        for (const game of games) {
            const chip = document.createElement('button');
            chip.className = 'set-chip' + (inSet ? '' : ' pool');
            if (this.pendingSwap === game) chip.classList.add('selected');
            chip.dataset.game = game;
            chip.textContent = GAME_INITIALS[game];
            chip.title = getGameTitle(game);
            chip.addEventListener('click', (e) => {
                e.stopPropagation();
                this.onChipClick(game, inSet);
            });
            row.appendChild(chip);
        }
        return row;
    }

    private onChipClick(game: GameType, inSet: boolean): void {
        if (this.pendingSwap === null) {
            this.pendingSwap = game;
            this.render();
            return;
        }
        const pendingInSet = this.selected.includes(this.pendingSwap);
        if (pendingInSet === inSet) {
            // Same row: just move the selection to the newly clicked chip.
            this.pendingSwap = game;
        } else {
            // Opposite rows: swap the out-of-set chip in for the in-set one.
            const drop = inSet ? game : this.pendingSwap;
            const add = inSet ? this.pendingSwap : game;
            const set = new Set(this.selected);
            set.delete(drop);
            set.add(add);
            this.selected = GAME_TYPES.filter(g => set.has(g));
            this.pendingSwap = null;
            this.onChange?.(this.getSelection());
        }
        this.render();
    }
}
